import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';

import { SparksAgentService } from '@/components/composite/SparksAgent/services/SparksAgentService';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import type { AiToolResult } from '@/components/composite/SparksAgent/types';

import Icon from '@/components/base/Icon';
import { SparksSourceChip } from '@/components/composite/SparksAgent/SparksSourceChip';

type SparksSourceBlockProps = {
    tools: AiToolResult[];
};

export function SparksSourceBlock({ tools }: SparksSourceBlockProps) {
    const { t } = useTranslation('sparks');
    const chips = SparksAgentService.getSourceChips(tools);
    const context = SparksAgentService.getAggregatedSourceContext(tools);
    const hasContext = Boolean(context.networks || context.periodComparison || context.metrics);

    return (
        <div className="mt-6 border-t border-[#dbe6ea] pt-6 dark:border-icoDarkMode-beton">
            <Disclosure defaultOpen={false}>
                {({ open }) => (
                    <div className={classNames('flex min-w-0 flex-col', open && 'gap-4')}>
                        <DisclosureButton
                            type="button"
                            className="flex w-full items-center justify-between gap-2 rounded-md py-0 text-left outline-none ring-icoBlue focus-visible:ring-2 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-icoDarkMode-anthracite">
                            <span className="flex min-w-0 items-center gap-1.5">
                                <span className="text-[10px] leading-none" aria-hidden>
                                    📖
                                </span>
                                <span className="font-GSemiBold text-[10px] uppercase leading-3 tracking-[0.1px] text-icoGray-500 dark:text-icoDarkMode-wolf">
                                    {t('results_based_on')}
                                </span>
                            </span>
                            <Icon
                                icon={open ? 'chevron-top' : 'chevron-bottom'}
                                className="size-3.5 shrink-0 text-icoGray-500 dark:text-icoDarkMode-wolf"
                                aria-hidden
                            />
                        </DisclosureButton>
                        <Transition
                            show={open}
                            enter="transition-all duration-200 ease-out"
                            enterFrom="transform grid-rows-[0fr] opacity-0"
                            enterTo="transform grid-rows-[1fr] opacity-100"
                            leave="transition-all duration-150 ease-in"
                            leaveFrom="transform grid-rows-[1fr] opacity-100"
                            leaveTo="transform grid-rows-[0fr] opacity-0">
                            <div className="grid">
                                <DisclosurePanel className="overflow-hidden">
                                    <div className="flex flex-col gap-4">
                                        {hasContext ? (
                                            <div className="text-icoGray-600 flex flex-col gap-2 font-GMedium text-[11px] leading-snug dark:text-icoDarkMode-wolf">
                                                {context.networks ? (
                                                    <p className="mb-0">
                                                        <span className="font-GSemiBold text-icoGray-500 dark:text-icoDarkMode-wolf">
                                                            {t('source_networks_label')}
                                                        </span>{' '}
                                                        {context.networks}
                                                    </p>
                                                ) : null}
                                                {context.periodComparison ? (
                                                    <p className="mb-0">
                                                        <span className="font-GSemiBold text-icoGray-500 dark:text-icoDarkMode-wolf">
                                                            {t('source_period_comparison_label')}
                                                        </span>{' '}
                                                        {context.periodComparison}
                                                    </p>
                                                ) : null}
                                                {context.metrics ? (
                                                    <p className="mb-0">
                                                        <span className="font-GSemiBold text-icoGray-500 dark:text-icoDarkMode-wolf">
                                                            {t('source_metrics_label')}
                                                        </span>{' '}
                                                        {context.metrics}
                                                    </p>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        <div className="flex flex-wrap gap-1">
                                            {chips.map((chip) => (
                                                <SparksSourceChip
                                                    key={chip.key}
                                                    label={chip.label}
                                                    href={chip.href}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </DisclosurePanel>
                            </div>
                        </Transition>
                    </div>
                )}
            </Disclosure>
        </div>
    );
}
